import { CircularProgress, Tooltip } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import { type FC, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import type { Database } from "../../../../../__definitions__/database-definitions";
import { useSupabaseCallback } from "../../../../../server/supabase/hooks";
import { XCircle } from "../../../../../styles/icons/x-circle";
import {
  type CustomEmailDomain,
  SupabaseCustomEmailDomainService,
} from "../../server/supabase-custom-email-domain-service";

export const DeleteButton: FC<{
  customEmailDomain: CustomEmailDomain;
  onDelete: () => void;
}> = ({ customEmailDomain, onDelete }) => {
  const { t } = useTranslation();
  const [removing, setRemoving] = useState(false);

  const handleDelete = useSupabaseCallback(
    async (
      { supabase },
      emailDomain: Database["public"]["Tables"]["custom_email_domain"]["Row"],
    ): Promise<void> => {
      setRemoving(true);

      const error = await new SupabaseCustomEmailDomainService(supabase).delete(emailDomain.id);

      if (!error) {
        toast.success(t("{{domain}} deleted successfully.", { domain: emailDomain.domain }));
        onDelete();
      } else {
        toast.error(
          t("Something went wrong when deleting {{domain}}", { domain: emailDomain.domain }),
        );
        setRemoving(false);
      }
    },
    [onDelete, t],
  );

  return (
    <GridActionsCellItem
      key="delete"
      icon={
        <Tooltip
          title={t("Delete {{domain}}", { domain: customEmailDomain.domain })}
          placement="right"
        >
          {removing ? (
            <CircularProgress size="small" />
          ) : (
            <XCircle data-testid="XCircleIcon" fontSize="small" />
          )}
        </Tooltip>
      }
      label={t("Delete {{domain}}", { domain: customEmailDomain.domain })}
      aria-label={`Delete ${customEmailDomain.domain}`}
      showInMenu={false}
      onClick={() => {
        void handleDelete(customEmailDomain).catch(console.error);
      }}
    />
  );
};
